import { api, TCustomAxiosResponse } from '..';
import { apiPaths } from '@/services/api/paths';
import { FEED_PAGE_SIZE } from '@/constants/contentFeed';
import {
  TContent,
  TContentAuthorProfile,
  TContentCreatorFacet,
  TContentDomain,
  TContentFilterOptions,
  TContentFilterOptionsResponse,
  TContentListResponse,
  TContentReportReason,
  TContentShareResponse,
  TContentType,
  TCreateContentPayload,
  TPersonalizedFeedResponse,
  TRecordFocusedViewPayload,
  TUpdateContentPayload,
} from '@/types/Content.types';

const MULTIPART_HEADERS = { 'Content-Type': 'multipart/form-data' } as const;

const MY_CONTENT_PAGE_SIZE = FEED_PAGE_SIZE;

// --- Public feed (published content the viewer can see) ---

export type TGetContentFeedParams = {
  page?: number;
  limit?: number;
  // Multi-select facets — sent comma-separated; the backend matches content tagged with ANY of them.
  domains?: TContentDomain[];
  subdomains?: string[];
  // Visibility: true = MyOmnia platform content, false = creator/org content. Omit for all.
  isPlatformContent?: boolean;
  // Only content authored by this user (a creator's profile page).
  authorUserId?: string;
  type?: TContentType;
  sort?: string;
};

export const getContentFeed = async ({
  page = 1,
  limit = FEED_PAGE_SIZE,
  domains,
  subdomains,
  isPlatformContent,
  authorUserId,
  type,
  sort,
}: TGetContentFeedParams = {}): Promise<TCustomAxiosResponse<TContent[]>> => {
  const params: Record<string, string | number | boolean> = { page, limit };
  if (domains?.length) params.domains = domains.join(',');
  if (subdomains?.length) params.subdomains = subdomains.join(',');
  if (isPlatformContent !== undefined) params.isPlatformContent = isPlatformContent;
  if (authorUserId) params.authorUserId = authorUserId;
  if (type) params.type = type;
  if (sort) params.sort = sort;

  const { data } = await api.get<TCustomAxiosResponse<TContent[]>>(apiPaths.content.root, { params });

  return data;
};

// Public profile + stats for a content author (their creator application + content count).
export const getContentAuthorProfile = async (userId: string): Promise<TContentAuthorProfile> => {
  const { data } = await api.get(apiPaths.content.author(userId));

  return data.data as TContentAuthorProfile;
};

// --- Personalized content feed algorithm ---

// Facets shared by the personalized feed, the deterministic /filtered endpoint and /search.
// `type` is multi-select (all three endpoints now accept an array).
export type TContentFacetParams = {
  domains?: TContentDomain[];
  subdomains?: string[];
  isPlatformContent?: boolean;
  authorUserId?: string;
  type?: TContentType[];
};

// Multi-select facets go over the wire comma-separated (the backend also accepts the
// repeated form); undefined/empty facets are omitted entirely rather than sent empty.
const buildFacetParams = (facets: TContentFacetParams): Record<string, string | number | boolean> => {
  const params: Record<string, string | number | boolean> = {};
  if (facets.domains?.length) params.domains = facets.domains.join(',');
  if (facets.subdomains?.length) params.subdomains = facets.subdomains.join(',');
  if (facets.isPlatformContent !== undefined) params.isPlatformContent = facets.isPlatformContent;
  if (facets.authorUserId) params.authorUserId = facets.authorUserId;
  if (facets.type?.length) params.type = facets.type.join(',');

  return params;
};

export type TGetPersonalizedFeedParams = TContentFacetParams & {
  // Opaque draw-snapshot cursor echoed back from the previous page. Never construct or
  // parse it — ordering and visibility are re-derived server-side for each page.
  cursor?: string;
  limit?: number;
};

// The ranked browse feed. The server decides personalized vs chronological (feature
// flag, rollout bucket, holdout) and returns an identical shape either way, so the
// client never branches on personalization.
export const getPersonalizedFeed = async ({
  cursor,
  limit = FEED_PAGE_SIZE,
  ...facets
}: TGetPersonalizedFeedParams = {}): Promise<TPersonalizedFeedResponse> => {
  const params: Record<string, string | number | boolean> = { limit, ...buildFacetParams(facets) };
  // Continuation is cursor-only — the server mints the draw seed on the cursor-less first
  // page and returns an opaque `nextCursor` to echo back for each subsequent page.
  if (cursor) params.cursor = cursor;

  const { data } = await api.get<TPersonalizedFeedResponse>(apiPaths.content.personalizedFeed, { params });

  return data;
};

export type TGetFilteredContentParams = TContentFacetParams & {
  page?: number;
  limit?: number;
  creator?: TContentCreatorFacet;
};

// Deterministic faceted browse — no draw rotation, so the same filters yield the same
// order across refreshes (unlike the ranked feed).
export const getFilteredContent = async ({
  page = 1,
  limit = FEED_PAGE_SIZE,
  creator,
  ...facets
}: TGetFilteredContentParams = {}): Promise<TContentListResponse> => {
  const params: Record<string, string | number | boolean> = { page, limit, ...buildFacetParams(facets) };
  if (creator) params.creator = creator;

  const { data } = await api.get<TContentListResponse>(apiPaths.content.filtered, { params });

  return data;
};

export type TGetContentSearchParams = TGetFilteredContentParams & {
  q?: string;
};

// Text-relevance search, deterministic. `q` is passed through verbatim and must never
// be logged, put in the URL, or forwarded to analytics/error reporting.
export const getContentSearch = async ({
  q,
  page = 1,
  limit = FEED_PAGE_SIZE,
  creator,
  ...facets
}: TGetContentSearchParams = {}): Promise<TContentListResponse> => {
  const params: Record<string, string | number | boolean> = { page, limit, ...buildFacetParams(facets) };
  if (creator) params.creator = creator;
  if (q) params.q = q;

  const { data } = await api.get<TContentListResponse>(apiPaths.content.search, { params });

  return data;
};

// Personalized filter options — the topic list is the viewer's lowest-scoring subdomains.
// The response is now correctly typed: a standard envelope wrapping
// ContentFilterOptionsDto, so no local narrowing is needed.
export const getContentFilterOptions = async (includeAllTopics = false): Promise<TContentFilterOptions> => {
  const { data } = await api.get<TContentFilterOptionsResponse>(apiPaths.content.filterOptions, {
    params: includeAllTopics ? { includeAllTopics: true } : {},
  });

  return data.data;
};

export type TGetFocusedFeedParams = {
  // Seeds a new focused session at the tapped card. Ignored once a cursor is present.
  anchorId?: string;
  tab?: 'ALL' | 'VIDEOS' | 'ARTICLES' | 'POSTS';
  cursor?: string;
  limit?: number;
};

// The full-screen "loop" stream. Read-only: fetching an item is NOT a view — a real
// view is reported separately via recordFocusedView() after genuine consumption.
export const getFocusedFeed = async ({
  anchorId,
  tab,
  cursor,
  limit,
}: TGetFocusedFeedParams = {}): Promise<TPersonalizedFeedResponse> => {
  const params: Record<string, string | number> = {};
  // The cursor is authoritative — anchorId/tab are ignored server-side when it's present.
  if (cursor) params.cursor = cursor;
  else {
    if (anchorId) params.anchorId = anchorId;
    if (tab) params.tab = tab;
  }
  if (limit !== undefined) params.limit = limit;

  const { data } = await api.get<TPersonalizedFeedResponse>(apiPaths.content.focusedFeed, { params });

  return data;
};

// The only signal that counts as a real consumption view. Fired once per item per loop
// session, at the moment the dwell threshold is crossed — never on prefetch.
export const recordFocusedView = async (payload: TRecordFocusedViewPayload): Promise<void> => {
  await api.post(apiPaths.content.focusedView, payload);
};

// --- Viewer reactions ---

export const likeContent = async (id: string): Promise<void> => {
  await api.post(apiPaths.content.like(id), {});
};

export const unlikeContent = async (id: string): Promise<void> => {
  await api.delete(apiPaths.content.like(id));
};

export const saveContent = async (id: string): Promise<void> => {
  await api.post(apiPaths.content.save(id), {});
};

export const unsaveContent = async (id: string): Promise<void> => {
  await api.delete(apiPaths.content.save(id));
};

// Returns the canonical share URL minted by the backend — which may differ from the
// in-app URL (public/share-safe, tracked, or short). Callers must copy THIS, not
// window.location. The response is now correctly typed (a standard envelope wrapping
// ContentShareResponseDto), so no local narrowing is needed.
export const shareContent = async (id: string, channel = 'copy_link'): Promise<TContentShareResponse> => {
  const { data } = await api.post<TCustomAxiosResponse<TContentShareResponse>>(apiPaths.content.share(id), {
    channel,
  });

  return data.data;
};

export const reportContent = async (id: string, reason: TContentReportReason, note?: string): Promise<void> => {
  await api.post(apiPaths.content.report(id), note ? { reason, note } : { reason });
};

// The backend permanently hides the item from every feed surface for the
// requester on the next read (candidate-gathering, search, filtered, and cursor replay);
// DELETE (unmarkContentNotInterested) restores it. Not a topic-level mute — item-scoped.
export const markContentNotInterested = async (id: string): Promise<void> => {
  await api.post(apiPaths.content.notInterested(id), {});
};

export const unmarkContentNotInterested = async (id: string): Promise<void> => {
  await api.delete(apiPaths.content.notInterested(id));
};

export type TGetSavedContentParams = {
  page?: number;
  limit?: number;
  type?: TContentType;
};

export const getSavedContent = async ({ page = 1, limit = FEED_PAGE_SIZE, type }: TGetSavedContentParams = {}): Promise<
  TCustomAxiosResponse<TContent[]>
> => {
  const params: Record<string, string | number> = { page, limit };
  if (type) params.type = type;

  const { data } = await api.get<TCustomAxiosResponse<TContent[]>>(apiPaths.content.saved, { params });

  return data;
};

// --- Creator content lifecycle ---

// Create a draft authored by the current creator. The server rejects isPlatformContent.
export const createContent = async (payload: TCreateContentPayload): Promise<TCustomAxiosResponse<TContent>> => {
  const { data } = await api.post(apiPaths.content.root, payload);

  return data;
};

export type TGetMyContentParams = {
  page?: number;
  limit?: number;
  // Format: `field:direction`, e.g. `updatedAt:desc`. See ListMyContentQueryDto.
  sort?: string;
};

// The creator's own content (drafts + published + removed).
export const getMyContent = async ({
  page = 1,
  limit = MY_CONTENT_PAGE_SIZE,
  sort,
}: TGetMyContentParams = {}): Promise<TCustomAxiosResponse<TContent[]>> => {
  const { data } = await api.get<TCustomAxiosResponse<TContent[]>>(apiPaths.content.me, {
    params: { page, limit, ...(sort ? { sort } : {}) },
  });

  return data;
};

export const getContentById = async (id: string): Promise<TContent> => {
  const { data } = await api.get(apiPaths.content.byId(id));

  return data.data as TContent;
};

export const updateContent = async (
  id: string,
  payload: TUpdateContentPayload,
): Promise<TCustomAxiosResponse<TContent>> => {
  const { data } = await api.patch(apiPaths.content.byId(id), payload);

  return data;
};

export const publishContent = async (id: string): Promise<TCustomAxiosResponse<TContent>> => {
  const { data } = await api.post(apiPaths.content.publish(id), {});

  return data;
};

export const unpublishContent = async (id: string): Promise<TCustomAxiosResponse<TContent>> => {
  const { data } = await api.post(apiPaths.content.unpublish(id), {});

  return data;
};

export const deleteContent = async (id: string): Promise<TCustomAxiosResponse<null>> => {
  const { data } = await api.delete(apiPaths.content.byId(id));

  return data;
};

export const uploadContentThumbnail = async (id: string, file: File): Promise<TCustomAxiosResponse<TContent>> => {
  const formData = new FormData();
  formData.append('file', file);

  const { data } = await api.post(apiPaths.content.thumbnail(id), formData, { headers: MULTIPART_HEADERS });

  return data;
};

// --- Video sources (reels) ---

// Issues a Mux direct-upload URL for a draft video; the browser uploads the file to it.
export const createMuxUploadUrl = async (id: string): Promise<{ uploadId: string; uploadUrl: string }> => {
  const { data } = await api.post(apiPaths.content.videoMuxUploadUrl(id), {});

  return data.data as { uploadId: string; uploadUrl: string };
};

export const setYoutubeVideoSource = async (id: string, url: string): Promise<TCustomAxiosResponse<TContent>> => {
  const { data } = await api.post(apiPaths.content.videoYoutube(id), { url });

  return data;
};

export const uploadArticleImage = async (
  id: string,
  file: File,
  sectionId: string,
  subsectionId?: string,
): Promise<TCustomAxiosResponse<TContent>> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('sectionId', sectionId);
  if (subsectionId) {
    formData.append('subsectionId', subsectionId);
  }

  const { data } = await api.post(apiPaths.content.articleImage(id), formData, { headers: MULTIPART_HEADERS });

  return data;
};
